import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../api/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SnackbarContext } from '../App';
import { Building2, Globe, MapPin, Users, Plus, ChevronRight } from "lucide-react";
import CompanyDetailDrawer from './CompanyDetailDrawer';
import CompanyCreateModal from './CompanyCreateModal';
import PageHeader from './ui/PageHeader';
import PageShell from './ui/PageShell';
import DataTable from './ui/DataTable';
import StatusBadge from './ui/StatusBadge';
import EmptyState from './ui/EmptyState';
import { Button } from './ui/button';

export default function CrmCompanies() {
    const showSnackbar = useContext(SnackbarContext);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // Detail Drawer State
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    
    // Creation Modal
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                limit: 25,
                search: search || ''
            });

            const { data } = await api.get(`/api/crm/companies?${params.toString()}`);
            setCompanies(data.data);
            setTotalPages(data.pagination.totalPages);
        } catch (error) {
            console.error('Error fetching companies:', error);
            showSnackbar('Error loading companies', 'error');
        } finally {
            setLoading(false);
        }
    }, [page, search, showSnackbar]);

// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const deepLinkId = searchParams.get('companyId');
        if (deepLinkId && !selectedCompanyId && !loading) {
            setSelectedCompanyId(deepLinkId);
            setIsDrawerOpen(true);
        }
    }, [searchParams, selectedCompanyId, loading]);

    useEffect(() => {
        fetchCompanies();
    }, [page, fetchCompanies]);

    return (
        <PageShell>
            <PageHeader 
                title="Companies"
                subtitle="Manage your workspace accounts and organizations."
                icon={Building2}
                actions={
                    <Button onClick={() => setIsCreateModalOpen(true)} className="bg-primary hover:bg-primary/90 shadow-lg border-0 gap-2 h-11 px-6 rounded-2xl font-bold transition-all active:scale-95">
                        <Plus className="w-5 h-5" /> New Company
                    </Button>
                }
            />

            <DataTable
                columns={[
                    {
                        header: 'Company Name',
                        accessor: 'name',
                        sortable: true,
                        render: (company) => (
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-sm uppercase shadow-sm">
                                    {company.name.substring(0, 1)}
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{company.name}</span>
                                    {company.industry && <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-black opacity-60 line-clamp-1">{company.industry}</span>}
                                </div>
                            </div>
                        )
                    },
                    {
                        header: 'Domain',
                        accessor: 'domain',
                        sortable: true,
                        render: (company) => (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium hover:text-foreground transition-colors">
                                <Globe className="w-3.5 h-3.5 opacity-50" />
                                {company.domain || company.website || '—'}
                            </div>
                        )
                    },
                    {
                        header: 'Industry',
                        accessor: 'industry',
                        sortable: true,
                        render: (company) => (
                            <StatusBadge variant="indigo">
                                {company.industry || 'Other'}
                            </StatusBadge>
                        )
                    },
                    {
                        header: 'Contacts',
                        accessor: 'contactCount',
                        sortable: true,
                        render: (company) => (
                            <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                                <Users className="w-3.5 h-3.5 text-primary" />
                                {company.contactCount || 0}
                            </div>
                        )
                    },
                    {
                        header: 'Location',
                        accessor: 'location',
                        sortable: true,
                        render: (company) => (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground truncate max-w-[150px]">
                                <MapPin className="w-3.5 h-3.5 opacity-40" />
                                {company.location || '—'}
                            </div>
                        )
                    },
                    {
                        header: '',
                        accessor: '_id',
                        align: 'right',
                        render: (company) => (
                            <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                        )
                    }
                ]}
                data={companies}
                loading={loading}
                emptyState={
                    <EmptyState 
                        title="No companies found"
                        description="Workspace companies group related contacts together, helping you scale account-based strategies."
                        icon={Building2}
                        action={
                            <Button 
                                onClick={() => setIsCreateModalOpen(true)} 
                                variant="outline" 
                                className="mt-2 border-border text-primary hover:bg-primary/5 rounded-xl font-bold"
                            >
                                <Plus className="w-4 h-4 mr-2" /> Create First Company
                            </Button>
                        }
                    />
                }
                pagination={{
                    page,
                    limit: 25,
                    totalRecords: companies.length * totalPages, // Rough estimate if totalRecords not provided
                    totalPages
                }}
                onPageChange={setPage}
                search={{
                    placeholder: "Search companies...",
                    value: search,
                    onChange: (val) => { setSearch(val); setPage(1); }
                }}
            />

            {/* Company Detail Drawer */}
            <CompanyDetailDrawer 
                isOpen={isDrawerOpen} 
                onClose={() => setIsDrawerOpen(false)} 
                companyId={selectedCompanyId} 
                onSaveSuccess={() => fetchCompanies()}
                onContactClick={(id) => { navigate(`/crm/contacts?focus=${id}`); }}
            />

            {/* Creation Modal */}
            <CompanyCreateModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={() => {
                    setPage(1);
                    fetchCompanies();
                }}
            />
        </PageShell>
    );
}
